import { apiFetch, apiFetchFormData, type ApiError } from "@/auth/apiClient";

export type UiCourseType = "recorded" | "live";
export type UiLessonType = "video" | "pdf" | "audio" | "live";

export const CourseTypeBackend = {
  recorded: "RECORDED",
  live: "LIVE",
} as const;

export const LessonTypeBackend = {
  video: "VIDEO",
  pdf: "PDF",
  audio: "AUDIO",
  live: "LIVE",
} as const;

/** JLPT levels (N5 → N1) for course tagging */
export const JLPT_COURSE_LEVELS = ["N5", "N4", "N3", "N2", "N1", "JFT A2"] as const;

export function formatCourseLevels(course: {
  levels?: string[] | null;
  level?: string | null;
}): string {
  if (course.levels?.length) return course.levels.join(", ");
  if (course.level) return course.level;
  return "-";
}

/** Same as `formatCourseLevels`, but replaces `"-"` with a caller-provided label. */
export function formatCourseLevelsOr(
  course: { levels?: string[] | null; level?: string | null },
  whenEmpty: string,
): string {
  const s = formatCourseLevels(course);
  return s === "-" ? whenEmpty : s;
}

export type CreatedCourse = {
  id: string;
};

export type UploadThumbnailResponse = {
  url: string;
  originalName: string;
  size: number;
  key?: string;
};

export type UploadLessonContentResponse = {
  url: string;
  originalName: string;
  size: number;
  /** S3 object key — store this as lesson.contentKey for secure signed URL delivery */
  key?: string;
};

type PresignLmsUploadResponse = {
  uploadUrl: string;
  url: string;
  originalName: string;
  size: number;
  key: string;
  headers: Record<string, string>;
};

/** Large lesson files go straight to S3 (presigned PUT); avoids API reverse-proxy body limits. */
async function uploadLessonMediaViaPresignedPut(
  category: "videos" | "audios" | "class-notes",
  file: File,
  onProgress?: (pct: number) => void
): Promise<UploadLessonContentResponse> {
  const contentType =
    category === "class-notes"
      ? file.type || "application/pdf"
      : file.type || "application/octet-stream";

  const presign = await apiFetch<PresignLmsUploadResponse>("/file-management/upload/presign", {
    method: "POST",
    json: {
      category,
      fileName: file.name,
      contentType,
      contentLength: file.size,
    },
    timeoutMs: 120_000,
  });

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", presign.uploadUrl, true);
    
    const ct = presign.headers["Content-Type"];
    if (ct) xhr.setRequestHeader("Content-Type", ct);

    if (onProgress && xhr.upload) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({
          url: presign.url,
          originalName: presign.originalName,
          size: presign.size,
          key: presign.key,
        });
      } else {
        const err: ApiError = {
          status: xhr.status,
          message: xhr.responseText?.trim().slice(0, 240) || xhr.statusText || "Upload to storage failed",
          body: xhr.responseText
        };
        reject(err);
      }
    };

    xhr.onerror = () => {
      reject({ status: 0, message: "Upload to storage failed (Network error)" } as ApiError);
    };

    xhr.send(file);
  });
}

export async function uploadCourseThumbnail(file: File): Promise<UploadThumbnailResponse> {
  const formData = new FormData();
  formData.append("file", file);
  return apiFetchFormData<UploadThumbnailResponse>(
    "/file-management/upload/thumbnail",
    formData,
  );
}

export async function uploadLessonVideo(file: File, onProgress?: (pct: number) => void): Promise<UploadLessonContentResponse> {
  return uploadLessonMediaViaPresignedPut("videos", file, onProgress);
}

export async function uploadLessonPdf(file: File, onProgress?: (pct: number) => void): Promise<UploadLessonContentResponse> {
  return uploadLessonMediaViaPresignedPut("class-notes", file, onProgress);
}

export async function uploadLessonAudio(file: File, onProgress?: (pct: number) => void): Promise<UploadLessonContentResponse> {
  return uploadLessonMediaViaPresignedPut("audios", file, onProgress);
}

export async function createCourse(dto: {
  title: string;
  description?: string;
  levels?: string[];
  type: "LIVE" | "RECORDED";
  price?: number;
  thumbnail?: string;
  isPublished?: boolean;
  startDate?: string;
  endDate?: string;
}): Promise<CreatedCourse> {
  return apiFetch<CreatedCourse>("/lms/courses", { method: "POST", json: dto });
}

export type Course = {
  id: string;
  title: string;
  description?: string | null;
  /** First level; mirrors `levels[0]` when set from the API */
  level?: string | null;
  levels?: string[] | null;
  type: "LIVE" | "RECORDED";
  price?: string;
  thumbnail?: string | null;
  isPublished: boolean;
  startDate?: string | null;
  endDate?: string | null;
  createdAt?: string;
  createdByUser?: { id: number; email: string };
  /** Present when listing as SYSTEM_ADMIN */
  enrollmentCount?: number;
};

export type CoursesListResponse = {
  items: Course[];
  total: number;
  page: number;
  limit: number;
};

export async function getCourses(params: { page?: number; limit?: number; search?: string; level?: string; type?: string } = {}): Promise<CoursesListResponse> {
  const page = params.page ?? 1;
  const limit = params.limit ?? 10;
  const search = params.search ?? "";
  const level = params.level ?? "";
  const type = params.type ?? "";

  const qObj: Record<string, string> = { page: String(page), limit: String(limit) };
  if (search) qObj.search = search;
  if (level) qObj.level = level;
  if (type) qObj.type = type;

  const qs = new URLSearchParams(qObj);
  return apiFetch<CoursesListResponse>(`/lms/courses?${qs.toString()}`, {
    method: "GET",
    timeoutMs: 15000,
  });
}

/** Courses assigned to the current user as a teacher */
export async function getTeachingCourses(): Promise<Course[]> {
  return apiFetch<Course[]>("/lms/courses/me/teaching", { method: "GET", timeoutMs: 15000 });
}

export type EnrolledStudentRow = {
  id: string;
  userId: number;
  enrolledAt?: string;
  user: {
    id: number;
    email: string;
    profile?: { firstName?: string | null; lastName?: string | null } | null;
  };
  progressPercentage?: string;
  lastAccessedAt?: string | null;
  /** Combined % across all CHAPTER exams in the course (best attempt; excludes unattempted exams). */
  combinedChapterExamPercentage?: string | null;
};

export async function getCourseStudents(courseId: string): Promise<EnrolledStudentRow[]> {
  return apiFetch<EnrolledStudentRow[]>(`/lms/courses/${courseId}/students`, { method: "GET" });
}

export async function updateCourse(
  courseId: string,
  dto: Partial<{
    title: string;
    description?: string;
    levels?: string[];
    type: "LIVE" | "RECORDED";
    price?: number;
    thumbnail?: string;
    isPublished?: boolean;
    startDate?: string;
    endDate?: string;
  }>,
): Promise<Course> {
  return apiFetch<Course>(`/lms/courses/${courseId}`, { method: "PATCH", json: dto });
}

export async function deleteCourse(courseId: string): Promise<void> {
  await apiFetch(`/lms/courses/${courseId}`, { method: "DELETE" });
}

export type CreatedChapter = { id: string };
export async function createChapter(courseId: string, dto: { title: string; order?: number }): Promise<CreatedChapter> {
  return apiFetch<CreatedChapter>(`/lms/courses/${courseId}/chapters`, {
    method: "POST",
    json: dto,
  });
}

export type CreatedLesson = { id: string };
export async function createLesson(
  courseId: string,
  chapterId: string,
  dto: {
    title: string;
    type: "VIDEO" | "PDF" | "AUDIO" | "LIVE";
    duration?: number;
    order?: number;
    contentUrl?: string;
    contentKey?: string;
    liveStartAt?: string;
    liveEndAt?: string;
    isPreview?: boolean;
    description?: string;
    objectives?: string;
  },
): Promise<CreatedLesson> {
  return apiFetch<CreatedLesson>(`/lms/courses/${courseId}/chapters/${chapterId}/lessons`, {
    method: "POST",
    json: dto,
  });
}

export async function updateChapter(
  courseId: string,
  chapterId: string,
  dto: Partial<{ title: string; order?: number }>,
): Promise<any> {
  return apiFetch<any>(`/lms/courses/${courseId}/chapters/${chapterId}`, { method: "PATCH", json: dto });
}

export async function reorderChapters(
  courseId: string,
  ids: string[],
): Promise<void> {
  await apiFetch(`/lms/courses/${courseId}/chapters/reorder`, {
    method: "PATCH",
    json: { ids },
  });
}

export async function deleteChapter(courseId: string, chapterId: string): Promise<void> {
  await apiFetch(`/lms/courses/${courseId}/chapters/${chapterId}`, { method: "DELETE" });
}

export async function updateLesson(
  courseId: string,
  chapterId: string,
  lessonId: string,
  dto: Partial<{
    title: string;
    type: "VIDEO" | "PDF" | "AUDIO" | "LIVE";
    contentUrl?: string | null;
    /** S3 object key — used for server-side signed URL generation */
    contentKey?: string | null;
    duration?: number;
    order?: number;
    liveStartAt?: string | null;
    liveEndAt?: string | null;
    isPreview?: boolean;
    description?: string | null;
    objectives?: string | null;
    resources?: string | null;
  }>,
): Promise<any> {
  return apiFetch<any>(
    `/lms/courses/${courseId}/chapters/${chapterId}/lessons/${lessonId}`,
    { method: "PATCH", json: dto }
  );
}

export async function reorderLessons(
  courseId: string,
  chapterId: string,
  ids: string[],
): Promise<void> {
  await apiFetch(`/lms/courses/${courseId}/chapters/${chapterId}/lessons/reorder`, {
    method: "PATCH",
    json: { ids },
  });
}

export async function deleteLesson(
  courseId: string,
  chapterId: string,
  lessonId: string,
): Promise<void> {
  await apiFetch(
    `/lms/courses/${courseId}/chapters/${chapterId}/lessons/${lessonId}`,
    { method: "DELETE" }
  );
}

export async function createLiveSession(
  courseId: string,
  dto: { chapterId?: string; title: string; startTime: string; endTime: string; meetingLink?: string },
): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(`/lms/courses/${courseId}/live-sessions`, {
    method: "POST",
    json: dto,
  });
}

export async function getPublicCourses(params: { page?: number; limit?: number; search?: string; level?: string; type?: string } = {}): Promise<CoursesListResponse> {
  const page = params.page ?? 1;
  const limit = params.limit ?? 6;
  const search = params.search ?? "";
  const level = params.level ?? "";
  const type = params.type ?? "";

  const qObj: Record<string, string> = { page: String(page), limit: String(limit) };
  if (search) qObj.search = search;
  if (level) qObj.level = level;
  if (type) qObj.type = type;

  const qs = new URLSearchParams(qObj);
  return apiFetch<CoursesListResponse>(`/lms/courses/public?${qs.toString()}`, { method: "GET" });
}

export async function enrollCourse(courseId: string): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(`/lms/courses/${courseId}/enroll`, { method: "POST", json: {} });
}

export type BkashInitResponse = {
  /** Present for paid courses — redirect to this URL */
  bkashURL: string | null;
  paymentID?: string;
  orderId?: string;
  /** Present for free courses — enrolled immediately */
  enrollmentId?: string;
};

export async function enrollCourseWithBkash(
  courseId: string,
  couponCode?: string,
): Promise<BkashInitResponse> {
  return apiFetch<BkashInitResponse>(`/lms/courses/${courseId}/pay/bkash`, {
    method: 'POST',
    json: couponCode ? { couponCode } : {},
  });
}

export type Enrollment = {
  id: string;
  enrolledAt?: string;
  course: Course;
};

export async function getMyEnrollments(): Promise<Enrollment[]> {
  return apiFetch<Enrollment[]>("/lms/enrollments", { method: "GET", timeoutMs: 15000 });
}

export type UserProgress = {
  id: string;
  userId: number;
  courseId: string;
  currentChapterId: string | null;
  progressPercentage: string;
  lastAccessedAt?: string | null;
  completedLessons?: string[];
  course?: Pick<Course, "id" | "title" | "level" | "levels" | "type" | "price" | "isPublished">;
};

export async function getMyProgress(): Promise<UserProgress[]> {
  return apiFetch<UserProgress[]>("/lms/progress", { method: "GET" });
}

export async function getCourse(courseId: string): Promise<Course & { chapters?: unknown[] }> {
  return apiFetch<Course>(`/lms/courses/${courseId}`, { method: "GET" }) as any;
}

export type LessonDetails = {
  id: string;
  title: string;
  type: "VIDEO" | "PDF" | "AUDIO" | "LIVE";
  duration?: number | null;
  order?: number;
  contentUrl?: string | null;
  /** ISO string from API for LIVE lessons */
  liveStartAt?: string | null;
  liveEndAt?: string | null;
  isPreview?: boolean;
  description?: string | null;
  objectives?: string | null;
  resources?: string | null;
};

export type ChapterDetails = {
  id: string;
  title: string;
  order?: number;
  isLocked?: boolean;
  lessons: LessonDetails[];
};

export async function getCourseChapters(courseId: string): Promise<ChapterDetails[]> {
  return apiFetch<ChapterDetails[]>(`/lms/courses/${courseId}/chapters`, { method: "GET" });
}

export async function getLesson(
  courseId: string,
  chapterId: string,
  lessonId: string,
): Promise<LessonDetails> {
  return apiFetch<LessonDetails>(
    `/lms/courses/${courseId}/chapters/${chapterId}/lessons/${lessonId}`,
    { method: "GET" },
  );
}

export type LiveSessionDetails = {
  id: string;
  courseId?: string;
  chapterId?: string | null;
  title: string;
  startTime: string;
  endTime: string;
  meetingLink?: string | null;
};

export async function getCourseLiveSessions(courseId: string): Promise<LiveSessionDetails[]> {
  return apiFetch<LiveSessionDetails[]>(`/lms/courses/${courseId}/live-sessions`, { method: "GET" });
}

export async function updateCourseProgress(
  courseId: string,
  body: { chapterId?: string | null },
): Promise<UserProgress> {
  return apiFetch<UserProgress>(`/lms/courses/${courseId}/progress`, { method: "PATCH", json: body });
}

export async function getCourseProgress(courseId: string): Promise<UserProgress> {
  return apiFetch<UserProgress>(`/lms/courses/${courseId}/progress`, { method: "GET" });
}

export async function markLessonComplete(courseId: string, lessonId: string): Promise<UserProgress> {
  return apiFetch<UserProgress>(`/lms/courses/${courseId}/progress/lessons/${lessonId}/complete`, { method: "POST" });
}

export async function resetChapterProgress(courseId: string, chapterId: string): Promise<UserProgress> {
  return apiFetch<UserProgress>(`/lms/courses/${courseId}/progress/chapters/${chapterId}/reset`, { method: "POST" });
}

export type CourseTeacherRow = {
  id: string;
  courseId: string;
  teacherId: number;
  teacher?: { id: number; email: string };
};

export async function getCourseTeachers(courseId: string): Promise<CourseTeacherRow[]> {
  return apiFetch<CourseTeacherRow[]>(`/lms/courses/${courseId}/teachers`, { method: "GET" });
}

export async function assignCourseTeacher(courseId: string, teacherId: number): Promise<CourseTeacherRow> {
  return apiFetch<CourseTeacherRow>(`/lms/courses/${courseId}/teachers`, {
    method: "POST",
    json: { teacherId },
  });
}

// ─── Video Security ────────────────────────────────────────────────────────────

export type VideoWatermark = {
  userId: number | null;
  email: string;
  name: string;
  issuedAt: string;
};

export type PlaybackTokenResponse = {
  signedUrl: string;
  playbackToken: string;
  expiresAt: string;
  expiresInSeconds: number;
  watermark: VideoWatermark;
};

/**
 * Request a short-lived signed S3 GET URL for a video lesson.
 * The URL expires in ~15 minutes. Only enrolled students / teachers / admins
 * can obtain a token. Must be called before each playback session.
 */
export async function requestVideoPlaybackToken(
  courseId: string,
  chapterId: string,
  lessonId: string,
): Promise<PlaybackTokenResponse> {
  return apiFetch<PlaybackTokenResponse>("/video-security/playback-token", {
    method: "POST",
    json: { lessonId, courseId, chapterId },
  });
}

/**
 * Report a client-side security event (devtools, screen capture, etc.)
 * to the backend audit log. Fire-and-forget; errors are silently ignored.
 */
export async function reportVideoSecurityEvent(
  lessonId: string,
  courseId: string,
  event: string,
): Promise<void> {
  try {
    await apiFetch("/video-security/report-event", {
      method: "POST",
      json: { lessonId, courseId, event },
    });
  } catch {
    // Silently ignore — don't interrupt playback for logging failures
  }
}
